import React, { useRef, useState } from "react";

import { useDeleteDataset, useHardDeleteDataset } from "api/api-hooks/datasets-hooks";
import { BaseDatasetDTO } from "api/model/dataset/BaseDatasetDTO";
import { DatasetInfo } from "api/model/dataset/DatasetInfo";
import ManageDatasetMappers from "components/dashboard/dataset-window/datasets-menu/dataset-mappers/ManageDatasetMappers";
import FindAndReplaceDialog from "components/dashboard/dataset-window/datasets-menu/find-and-replace-dialog/FindAndReplaceDialog";
import DatasetUsageSearchDialog from "components/fragments/dataset-usage-search/DatasetUsageSearchDialog";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { easasConfirmDialog } from "components/fragments/easas-global-fragment/EasasGlobalConfirmDialog";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Menu } from "primereact/menu";
import { MenuItem } from "primereact/menuitem";
import { T } from "services/lang/LanguageService";
import { DweenTimeUnitEnum } from "utils/time-utils";
import { useHasPermissions } from "hooks/useHasPermissions";
import AddDataDialog from "./add-data-dialog/AddDataDialog";
import ClearDatasetDialog from "./clear-dataset-dialog/ClearDatasetDialog";
import CreateDatasetDialog from "./create-dataset-dialog/CreateDatasetDialog";
import DatasetGroupsSettings from "./dataset-groups/DatasetGroupsSettings";
import EditDatasetDialog from "./edit-dataset-dialog/EditDatasetDialog";
import ObjectDatasetTable from "./object-dataset/ObjectDatasetTable";

interface IDatasetsMenuProps {
  selectedDatasets: Array<DatasetInfo>;
  setSelectedDatasets: (arr: Array<DatasetInfo>) => void;
}

type DialogVisibilities = {
  createDataset: boolean;
  clearData: boolean;
  addData: boolean;
  editDataset: boolean;
  findAndReplace: boolean;
  manageMappers: boolean;
  searchDatasets: boolean;
  objectDataset: boolean;
  datasetGroups: boolean;
  setDatasetToGroups: boolean;
};

const defaultDialogVisibilities: DialogVisibilities = {
  createDataset: false,
  clearData: false,
  addData: false,
  editDataset: false,
  findAndReplace: false,
  manageMappers: false,
  searchDatasets: false,
  objectDataset: false,
  datasetGroups: false,
  setDatasetToGroups: false,
};

const DatasetsMenu = ({ selectedDatasets, setSelectedDatasets }: IDatasetsMenuProps) => {
  const [dialogVisibility, setDialogVisibility] = useState<DialogVisibilities>(defaultDialogVisibilities);

  const hasSoftDeletePerm = useHasPermissions("DATASET_DELETE_SOFT");
  const hasHardDeletePerm = useHasPermissions("DATASET_DELETE_HARD");
  const hasObjectDatasetManagementPerm = useHasPermissions("OBJECT_DATASET_MANAGEMENT");

  const { mutateAsync: softDeleteMut } = useDeleteDataset();
  const { mutateAsync: hardDeleteMut } = useHardDeleteDataset();

  const menu = useRef<Menu | null>(null);
  const items: MenuItem[] = [
    {
      label: T("label.dataset-options"),
      items: [
        {
          label: T("action.create-dataset"),
          icon: "pi pi-plus-circle",
          command: () => setDialogVisibility({ ...defaultDialogVisibilities, createDataset: true }),
        },
        {
          label: T("label.search-datasets-usage"),
          icon: "pi pi-search",
          command: () => setDialogVisibility({ ...defaultDialogVisibilities, searchDatasets: true }),
        },
        {
          label: T("action.edit-dataset"),
          icon: "pi pi-pencil",
          command: () => setDialogVisibility({ ...defaultDialogVisibilities, editDataset: true }),
          disabled: selectedDatasets.length === 0,
        },
        {
          label: T("action.set-group"),
          icon: "pi pi-sitemap",
          command: () =>
            setDialogVisibility({
              ...defaultDialogVisibilities,
              datasetGroups: true,
              setDatasetToGroups: true,
            }),
          disabled: selectedDatasets.length === 0,
        },
        {
          label: T("action.find-and-replace"),
          icon: "pi pi-search",
          command: () => setDialogVisibility({ ...defaultDialogVisibilities, findAndReplace: true }),
          disabled: selectedDatasets.length === 0,
        },
        {
          label: T("action.delete-dataset"),
          icon: "pi pi-trash",
          command: () => confirmDelete("SOFT"),
          disabled: selectedDatasets.length === 0 || !hasSoftDeletePerm,
        },
        {
          label: T("action.hard-delete-dataset"),
          icon: "pi pi-trash",
          command: () => confirmDelete("HARD"),
          disabled: selectedDatasets.length === 0 || !hasHardDeletePerm,
        },
      ],
    },
    {
      label: T("label.dataset-options"),
      items: [
        {
          label: T("action.add-data"),
          icon: "pi pi-plus",
          command: () => setDialogVisibility({ ...defaultDialogVisibilities, addData: true }),
          disabled: selectedDatasets.length !== 1,
        },
        {
          label: T("action.clear-data"),
          icon: "pi pi-minus-circle",
          command: () => setDialogVisibility({ ...defaultDialogVisibilities, clearData: true }),
          disabled: selectedDatasets.length === 0,
        },
      ],
    },
    {
      label: T("word.settings"),
      items: [
        {
          label: T("action.manage-mappings"),
          icon: "pi pi-pencil",
          command: () => setDialogVisibility({ ...defaultDialogVisibilities, manageMappers: true }),
        },
        {
          label: T("label.object-dataset"),
          icon: "pi pi-pencil",
          command: () => setDialogVisibility({ ...defaultDialogVisibilities, objectDataset: true }),
          visible: hasObjectDatasetManagementPerm,
        },
        {
          label: T("label.dataset-groups"),
          icon: "pi pi-users",
          command: () => setDialogVisibility({ ...defaultDialogVisibilities, datasetGroups: true }),
        },
      ],
    },
  ];

  const confirmDelete = (type: "SOFT" | "HARD") => {
    easasConfirmDialog({
      header: T("confirm-dialog.report-table.delete-confirmation"),
      message: `${T("confirm-dialog.datasets.do-you-want-to-delete-these-datasets")} ${type === "HARD" ? " This action deletes data and is irreversible." : ""}`,
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      confirmedCallback: () => deleteHandler(type),
    });
  };

  const deleteHandler = async (type: "SOFT" | "HARD") => {
    const identifiers = selectedDatasets.map(dataset => dataset.identifier);
    const deleteMut = type === "SOFT" ? softDeleteMut : hardDeleteMut;
    const results = await Promise.allSettled(identifiers.map(id => deleteMut(id!)));

    if (results.every(result => result.status === "fulfilled")) {
      easasAlert({
        summary: T("alert.success"),
        detail: T("alert.datasets.datasets-were-deleted-successfully"),
        severity: "success",
      });
    } else {
      easasAlert({
        summary: T("alert.error"),
        detail: T("error.dataset-failed-to-delete"),
        severity: "error",
      });
    }
    setSelectedDatasets([]);
  };

  const closeDialog = () => setDialogVisibility(defaultDialogVisibilities);

  return (
    <>
      <div>
        <Menu model={items} popup ref={menu} id="popup_menu" />
        <Button
          className="p-button-outlined p-button-secondary"
          icon="pi pi-bars"
          onClick={event => menu.current?.toggle(event)}
          aria-controls="popup_menu"
          aria-haspopup
        />
      </div>
      <DatasetUsageSearchDialog visible={dialogVisibility.searchDatasets} closeDialog={closeDialog} />
      <Dialog
        style={{ width: "min(100vw, 800px)", minHeight: "250px" }}
        header={T("action.create-new-dataset")}
        visible={dialogVisibility.createDataset}
        onHide={closeDialog}
        resizable
        blockScroll
        modal={false}
      >
        <CreateDatasetDialog closeDialog={closeDialog} />
      </Dialog>
      <Dialog
        style={{ width: "min(100vw, 1200px)", height: "400x" }}
        header={T("action.edit-dataset")}
        visible={dialogVisibility.editDataset}
        onHide={closeDialog}
        resizable
        modal={false}
      >
        <EditDatasetDialog
          closeDialog={closeDialog}
          selectedDatasets={selectedDatasets}
          setSelectedDatasets={setSelectedDatasets}
        />
      </Dialog>
      <Dialog
        style={{ width: "min(100vw, 400px)", height: "400x" }}
        header={T("action.find-and-replace")}
        visible={dialogVisibility.findAndReplace}
        onHide={closeDialog}
        resizable
      >
        <FindAndReplaceDialog
          closeDialog={closeDialog}
          selectedDatasets={selectedDatasets}
          setSelectedDatasets={setSelectedDatasets}
        />
      </Dialog>
      <Dialog
        style={{ width: "min(100vw, 800px)", maxHeight: "600px" }}
        header={T("action.manage-mappers")}
        visible={dialogVisibility.manageMappers}
        onHide={closeDialog}
        resizable
      >
        <ManageDatasetMappers />
      </Dialog>
      <Dialog
        style={{ width: "min(100vw, 800px)", height: "600px" }}
        header={T("label.dataset-groups")}
        visible={dialogVisibility.datasetGroups}
        onHide={closeDialog}
        resizable
      >
        <DatasetGroupsSettings
          selectedDatasets={selectedDatasets as BaseDatasetDTO[]}
          set={dialogVisibility.setDatasetToGroups}
          closeDialog={closeDialog}
        />
      </Dialog>
      <Dialog
        style={{ width: "min(100vw, 400px)", height: "400x" }}
        header={T("action.clear-dataset-data")}
        visible={dialogVisibility.clearData}
        onHide={closeDialog}
        resizable={false}
        modal
      >
        <ClearDatasetDialog closeDialog={closeDialog} selectedDatasets={selectedDatasets} />
      </Dialog>
      <Dialog
        onHide={closeDialog}
        style={{ width: "min(100vw, 600px)", height: "400x" }}
        visible={dialogVisibility.addData}
        header={T("action.add-data-to-dataset")}
        modal
      >
        <AddDataDialog
          selectedDatasets={selectedDatasets}
          firstLoadDate={new Date()}
          dateFrequency={DweenTimeUnitEnum.SECONDS}
          closeDialog={closeDialog}
        />
      </Dialog>
      <Dialog
        onHide={closeDialog}
        style={{ width: "min(100vw, 600px)", height: "400x" }}
        visible={dialogVisibility.objectDataset}
        header={T("label.object-dataset")}
        modal
      >
        <ObjectDatasetTable />
      </Dialog>
    </>
  );
};

export default DatasetsMenu;
